import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';
import './ContactPage.css';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    // In production, this would send to a backend endpoint
    console.log('Contact form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 3000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="public-page">
      <SEO
        title="Contact Us - Get Help With Hangrily"
        description="Have a question, feedback, or need support? Contact the Hangrily team. We're here to help you get the most out of the restaurant decision app."
        path="/contact"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
            { '@type': 'ListItem', position: 2, name: 'Contact', item: 'https://hangrily.app/contact' }
          ]
        }}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>Contact Us</h1>
          <p className="hero-subtitle">
            Have questions, feedback, or need support? We'd love to hear from you.
          </p>
        </section>

        <section className="contact-content">
          <div className="contact-grid">
            <div className="contact-info-section">
              <h2>Get in Touch</h2>
              <p>
                We're here to help! Whether you have a question about features, need technical support, 
                or just want to share feedback, our team is ready to assist you.
              </p>

              <div className="contact-methods">
                <div className="contact-method">
                  <div className="method-icon">📧</div>
                  <div className="method-details">
                    <h3>Email Support</h3>
                    <p>For general inquiries and support</p>
                    <a href="mailto:info@fraxo.app">info@fraxo.app</a>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="method-icon">🔒</div>
                  <div className="method-details">
                    <h3>Privacy & Legal</h3>
                    <p>For privacy concerns and legal matters</p>
                    <a href="mailto:info@fraxo.app">info@fraxo.app</a>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="method-icon">💼</div>
                  <div className="method-details">
                    <h3>Business Inquiries</h3>
                    <p>For partnerships and press</p>
                    <a href="mailto:info@fraxo.app">info@fraxo.app</a>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="method-icon">🐛</div>
                  <div className="method-details">
                    <h3>Bug Reports</h3>
                    <p>Found a bug? Let us know!</p>
                    <a href="mailto:info@fraxo.app">info@fraxo.app</a>
                  </div>
                </div>
              </div>

              <div className="response-time">
                <h3>Response Time</h3>
                <p>
                  We typically respond within 24-48 hours during business days. For urgent issues, 
                  please mark your email as "URGENT" in the subject line.
                </p>
              </div>
            </div>

            <div className="contact-form-section">
              <h2>Send Us a Message</h2>
              {submitted ? (
                <div className="success-message">
                  <div className="success-icon">✓</div>
                  <h3>Message Sent!</h3>
                  <p>Thank you for contacting us. We'll get back to you soon.</p>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="subject">Subject *</label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select a subject</option>
                      <option value="support">Technical Support</option>
                      <option value="feedback">Feedback & Suggestions</option>
                      <option value="bug">Bug Report</option>
                      <option value="account">Account Issues</option>
                      <option value="privacy">Privacy Concerns</option>
                      <option value="partnership">Partnership Inquiry</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="6"
                      placeholder="Tell us how we can help..."
                    ></textarea>
                  </div>

                  <button type="submit" className="btn btn-primary btn-lg">
                    Send Message
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        <section className="faq-quick-links">
          <h2 className="section-title">Quick Answers</h2>
          <p>Looking for quick help? Check out these resources:</p>
          <div className="quick-links-grid">
            <Link to="/faq" className="quick-link-card">
              <div className="quick-link-icon">❓</div>
              <h3>FAQ</h3>
              <p>Find answers to common questions</p>
            </Link>
            <Link to="/how-it-works" className="quick-link-card">
              <div className="quick-link-icon">📖</div>
              <h3>How It Works</h3>
              <p>Learn how to use Hangrily</p>
            </Link>
            <Link to="/features" className="quick-link-card">
              <div className="quick-link-icon">⭐</div>
              <h3>Features</h3>
              <p>Explore all our features</p>
            </Link>
            <Link to="/privacy" className="quick-link-card">
              <div className="quick-link-icon">🔒</div>
              <h3>Privacy Policy</h3>
              <p>How we protect your data</p>
            </Link>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
